import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
  TextArea,
} from '@labrav/react-components'
import { Dispatch, SetStateAction } from 'react'
import { useDispatch, useSelector } from '../../../store'
import { Form, Formik, FormikProps } from 'formik'
import { OfferRecipients } from '../../../types/flyout'
import { offerRecipientReminderValidation } from '../../utils/PrivateOfferContainerFormikValidationSchema'
import { RecipientCheckBoxListItem } from '../RecipientCheckBoxListItem/RecipientCheckBoxListItem'
import { useStyles } from './OfferReminderTheBuyer.styles'
import { Typography } from '@mui/material'
import { sendReminder } from '../../../modules/privateOffer/action'
import { noop } from 'lodash'
import { CloudMarketplace } from '../../../types'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

type OfferRecipientsBuyerReminder = OfferRecipients & {
  value: boolean
}

type BuyerReminderValuesType = {
  offerRecipients: OfferRecipientsBuyerReminder[]
  customMessage: string
}

export const OfferReminderTheBuyer = ({
  cloudMarketplace,
  partnerId,
  openReminderTheBuyerModal,
  setOpenReminderTheBuyerModal,
  offerRecipients,
  offerId,
  handleSubmit,
  title,
  isSubmitLoading,
}: {
  cloudMarketplace: CloudMarketplace
  partnerId: string
  openReminderTheBuyerModal: boolean
  setOpenReminderTheBuyerModal: Dispatch<SetStateAction<boolean>>
  offerRecipients: OfferRecipients[]
  offerId: string
  handleSubmit?: (data: Record<string, unknown>) => void
  title?: string
  isSubmitLoading?: boolean
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_FLYOUT_OFFER)
  )

  const handleCloseModal = () => {
    setOpenReminderTheBuyerModal(false)
  }

  const handleConfirmModal = (values: BuyerReminderValuesType) => {
    const offerRecipientsToSend = values?.offerRecipients
      .filter(recipient => recipient?.value)
      .map(recipient => {
        const { firstName, lastName, email, title } = recipient
        return {
          firstName,
          lastName,
          email,
          title,
        }
      })
    if (handleSubmit) {
      handleSubmit({
        recipients: offerRecipientsToSend,
        customMessage: values?.customMessage,
      })
    } else {
      dispatch(
        sendReminder({
          data: {
            recipients: offerRecipientsToSend,
            customMessage: values?.customMessage,
          },
          privateOfferId: offerId,
          callBack: handleCloseModal,
          partnerId,
          cloudMarketplace,
        })
      )
    }
  }

  const initialValuesBuyerReminder: BuyerReminderValuesType = {
    offerRecipients: offerRecipients?.map(recipient => ({
      ...recipient,
      value: true,
    })),
    customMessage: '',
  }

  return (
    <ModalConfirmV2
      title={title || 'Remind the buyers'}
      open={openReminderTheBuyerModal}
      acceptLoading={isLoading}
      onDecline={handleCloseModal}
      variant={ModalSizeVariants.large}
      content={
        <Formik
          initialValues={initialValuesBuyerReminder}
          validationSchema={offerRecipientReminderValidation}
          validateOnMount={true}
          onSubmit={value => {
            handleConfirmModal(value)
          }}
          enableReinitialize
        >
          {formikProps => {
            const handleSubmitForm = async () => {
              await formikProps?.submitForm()
            }
            const { values, errors, touched, handleChange, handleBlur } =
              formikProps

            const isSendDisable = !values?.offerRecipients?.filter(
              recipient => recipient?.value
            )?.length
            return (
              <Form>
                <div className={classes.selectRecipient}>
                  Select offer recipients
                </div>
                <div
                  className={classes.root}
                  data-testid={'offer-reminder-buyer'}
                >
                  {values?.offerRecipients?.map((offerRecipientItem, index) => (
                    <RecipientCheckBoxListItem
                      key={`offerRecipients[${index}].value`}
                      keyName={`offerRecipients[${index}].value`}
                      hideBorder={values?.offerRecipients?.length - 1 === index}
                      recipient={offerRecipientItem}
                      formik={
                        formikProps as unknown as FormikProps<
                          Record<string, unknown>
                        >
                      }
                    />
                  ))}
                </div>
                <div className={classes.sectionContainer}>
                  <div className={classes.textContainer}>
                    <div className={classes.sectionHeadingContainer}>
                      {' '}
                      <Typography className={classes.sectionHeading}>
                        Custom message
                      </Typography>
                      <Typography className={classes.sectionText}>
                        Optional
                      </Typography>
                    </div>
                    <Typography className={classes.sectionText}>
                      You can add a custom message that will appear at the top
                      of the email containing the offer details.
                    </Typography>
                  </div>
                  <TextArea
                    keyName="customMessage"
                    title="Add a custom message"
                    rows={10}
                    maxNumber={1000}
                    height={100}
                    type="string"
                    placeHolder="Add a custom message"
                    value={(values.customMessage || '') as string}
                    isError={!!(errors.customMessage && touched.customMessage)}
                    onChange={handleChange}
                    isReadOnly={false}
                    isRequired={false}
                    errorText={
                      (!!touched.customMessage && errors.customMessage) || ''
                    }
                    showSpinner={false}
                    onBlur={handleBlur}
                  />
                </div>

                <div className={classes.rowRightItem}>
                  <ButtonV2
                    data-testid="send-reminder"
                    smallVariant
                    styleType="primary"
                    onClick={handleSubmitForm}
                    loading={isSubmitLoading || isLoading}
                    disabled={isSendDisable}
                  >
                    Send reminder email
                  </ButtonV2>
                </div>
              </Form>
            )
          }}
        </Formik>
      }
      icon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '14px', height: '14px' }}
        />
      }
      hideCancelIcon={false}
      hideCancelButton={true}
      hideAcceptButton={true}
      onAccept={noop}
    />
  )
}
